/**
 * AddInsurancePolicyModal — chooser invoked from the Agreement Insurance tab
 * to add a Policy to the Agreement.
 *
 * Three paths:
 *   1. **Create new Policy** (#274 Wave A, 2026-06-08) — navigates to
 *      /account-insurance/new with the Agreement ID prefilled.
 *   2. **Attach existing limbo Policy** (#274 Wave A) — picker over Policies
 *      in Inactive state with agreement_id IS NULL. Calls POST /attach
 *      (direct mutation, Compliance/Admin only). Indirect Accounts stay
 *      Inactive (asymmetric reversal).
 *   3. **Move from another Agreement** (#104 Wave B, 2026-06-08) — picker
 *      over Active Policies currently attached to a DIFFERENT Agreement.
 *      Calls POST /move via maker-checker. Compliance/Admin auto-apply;
 *      RM filings queue a pending CR. Active Indirect Accounts cascade
 *      to Inactive (same as detach). Footprint shown for confirmation.
 *
 * The modal is presentation-only; data fetching + mutations live in
 * useAccountInsurance.ts.
 */
import {
  Alert, Badge, Button, Card, Group, Modal, Select, Stack, Text, Title,
} from '@mantine/core';
import {
  IconAlertCircle, IconAlertTriangle, IconArrowsRightLeft, IconLink,
  IconPlus, IconShieldPlus,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import type { UUID } from '../api/types';
import {
  useAccountInsuranceList, useAttachPolicy,
  useMovePolicy, useMovePolicyPreview,
} from '../hooks/useAccountInsurance';
import { useAlertModal } from './AlertModalProvider';
import { notifications } from '@mantine/notifications';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The Agreement we are adding a Policy to. The Agreement is assumed to
   *  be Active — caller guards this (the trigger button is gated on
   *  non-Active Agreements). */
  agreementId: UUID;
  /**
   * C-111 (Session A4, 2026-06-10): fired after a Pattern B commit lands
   * (attach or move success). When this modal is reachable only from
   * inside the page's Edit mode, the page passes a callback that exits
   * Edit mode — otherwise the user is left in "zombie" Edit (nothing
   * dirty, Save greyed). Optional: callsites outside an Edit transaction
   * can omit it. The create-new path navigates away and doesn't fire it.
   * NOTE: irrelevant when the staging callbacks below are provided —
   * staged paths never commit from the modal.
   */
  onCommitted?: () => void;
  /**
   * #21 / #19-B (2026-06-10, Pedro's decision): Pattern A staging paths.
   * When provided, Confirm does NOT call the API — the picked policy (and
   * for move, the stage-time footprint plan + labels) is handed back to
   * the page, which stages it on the Edit transaction and commits on
   * Save. The server re-validates a staged move plan at commit time
   * (stale-plan 409), so the preview shown here remains trustworthy.
   */
  onStageAttach?: (policy_id: UUID, label: string | null) => void;
  onStageMove?: (
    policy_id: UUID,
    plan: Record<string, unknown>,
    label: string | null,
    sourceLabel: string | null,
  ) => void;
}

type Path = 'choose' | 'create' | 'attach' | 'move';

export function AddInsurancePolicyModal({
  opened, onClose, agreementId, onCommitted, onStageAttach, onStageMove,
}: Props) {
  const [path, setPath] = useState<Path>('choose');
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>('');
  const navigate = useNavigate();
  const attachMut = useAttachPolicy();
  const moveMut = useMovePolicy();
  const { showAlert } = useAlertModal();

  // Limbo Policies (for the attach path) — fetched only when needed.
  const limboQ = useAccountInsuranceList({
    unassigned_only: true,
    status: ['Inactive'],
    limit: 200,
  });

  // Active Policies on OTHER Agreements (for the move path).
  // Server-side filter by status=Active; client-side filter out policies
  // already on this Agreement. At Pedro's firm scale (~200 policies max),
  // client-side filtering is fine.
  const activeQ = useAccountInsuranceList({
    status: ['Active'],
    limit: 200,
  });
  const moveCandidates = (activeQ.data?.items ?? [])
    .filter((p) => p.agreement_id !== null && p.agreement_id !== agreementId);

  // Live preview for the selected move candidate.
  const movePreviewQ = useMovePolicyPreview(
    path === 'move' ? (selectedPolicyId as UUID) || undefined : undefined,
    path === 'move' ? agreementId : undefined,
    path === 'move' && !!selectedPolicyId,
  );

  function reset() {
    setPath('choose');
    setSelectedPolicyId('');
  }
  function handleClose() {
    reset();
    onClose();
  }

  function handleCreateNew() {
    handleClose();
    navigate(`/account-insurance/new?agreement_id=${agreementId}`);
  }

  function handleAttach() {
    if (!selectedPolicyId) return;
    // #21: staging path — hand the pick back to the page; Save commits.
    if (onStageAttach) {
      const picked = (limboQ.data?.items ?? []).find((p) => p.id === selectedPolicyId);
      const label = picked ? `${picked.insurer} · ${picked.policy_number}` : null;
      onStageAttach(selectedPolicyId as UUID, label);
      handleClose();
      return;
    }
    attachMut.mutate(
      { id: selectedPolicyId as UUID, agreementId },
      {
        // 2026-06-10: attach is maker-checker now — surface the RM
        // pending case on this legacy immediate path too.
        onSuccess: (r) => {
          if (r.pending) {
            notifications.show({
              color: 'orange',
              message: 'Attach filed for approval — the policy stays detached until Compliance approves.',
            });
          }
          handleClose();
          onCommitted?.();
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            showAlert({
              color: 'red',
              title: 'Cannot attach Policy',
              message: err.detail,
            });
          }
        },
      },
    );
  }

  function handleMove() {
    if (!selectedPolicyId || !movePreviewQ.data) return;
    const fp = movePreviewQ.data;
    const plan = {
      policy_id: fp.policy_id,
      source_agreement_id: fp.source_agreement_id,
      target_agreement_id: fp.target_agreement_id,
      policy_current_status: fp.policy_current_status,
      indirect_account_ids: fp.indirect_accounts.map((it) => it.id),
      summary: fp.summary,
    };
    // #21: staging path — the stage-time plan rides with the op; the
    // server re-validates it at Save (stale-plan 409 → Failed + re-pick).
    if (onStageMove) {
      onStageMove(
        selectedPolicyId as UUID,
        plan,
        fp.policy_label ?? null,
        fp.source_agreement_label ?? null,
      );
      handleClose();
      return;
    }
    moveMut.mutate(
      { id: selectedPolicyId as UUID, body: { move_plan: plan } },
      {
        onSuccess: (r) => {
          if (r.pending) {
            notifications.show({
              color: 'blue',
              title: 'Move filed for approval',
              message: 'Compliance will review the move and apply it. You can track progress in My Change Requests.',
            });
          } else {
            notifications.show({
              color: 'green',
              title: 'Policy moved',
              message: fp.source_agreement_label
                ? `${fp.policy_label} moved from ${fp.source_agreement_label}.`
                : `${fp.policy_label} attached.`,
            });
          }
          handleClose();
          onCommitted?.();
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            showAlert({
              color: 'red',
              title: err.status === 409 ? 'Move plan is stale' : 'Cannot move Policy',
              message: err.detail,
            });
          }
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconShieldPlus size={20} />
          <Title order={4}>Add Insurance Policy</Title>
        </Group>
      }
    >
      {path === 'choose' && (
        <Stack>
          <Text size="sm" c="dimmed">
            Choose how you would like to add an insurance Policy to this Agreement.
          </Text>

          <Card withBorder padding="md" radius="sm" onClick={() => setPath('create')}
            style={{ cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Group gap="xs"><IconPlus size={16} /><Text fw={600}>Create new Policy</Text></Group>
                <Text size="sm" c="dimmed">
                  Start a fresh Policy bound to this Agreement. You will fill
                  in insurer, policy number, fund details, etc.
                </Text>
              </Stack>
            </Group>
          </Card>

          <Card withBorder padding="md" radius="sm" onClick={() => setPath('attach')}
            style={{ cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Group gap="xs"><IconLink size={16} /><Text fw={600}>Attach existing limbo Policy</Text></Group>
                <Text size="sm" c="dimmed">
                  Re-bind a Policy that is currently in limbo (Inactive,
                  detached from any Agreement). Indirect Accounts owned by
                  the Policy remain Inactive after attach — they must be
                  reactivated individually.
                </Text>
              </Stack>
            </Group>
          </Card>

          <Card withBorder padding="md" radius="sm" onClick={() => setPath('move')}
            style={{ cursor: 'pointer' }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={4}>
                <Group gap="xs"><IconArrowsRightLeft size={16} /><Text fw={600}>Move from another Agreement</Text></Group>
                <Text size="sm" c="dimmed">
                  Move an Active Policy from a different Agreement to this
                  one. The Policy's Active Indirect Accounts cascade to
                  Inactive. Routes through maker-checker — RM filings queue
                  a pending Change Request; Compliance / Admin auto-apply.
                </Text>
              </Stack>
            </Group>
          </Card>

          <Group justify="flex-end" pt="sm">
            <Button variant="default" onClick={handleClose}>Cancel</Button>
          </Group>
        </Stack>
      )}

      {path === 'create' && (
        <Stack>
          <Alert color="blue" icon={<IconAlertCircle size={16} />}>
            You will be taken to the new-Policy form with this Agreement
            prefilled. Unsaved changes on the current Agreement form will be
            preserved if you cancel and come back.
          </Alert>
          <Group justify="space-between">
            <Button variant="default" onClick={() => setPath('choose')}>Back</Button>
            <Button leftSection={<IconPlus size={14} />} onClick={handleCreateNew}>
              Open new-Policy form
            </Button>
          </Group>
        </Stack>
      )}

      {path === 'attach' && (
        <Stack>
          {limboQ.isLoading && <Text c="dimmed" size="sm">Loading limbo Policies…</Text>}

          {!limboQ.isLoading && (limboQ.data?.items.length ?? 0) === 0 && (
            <Alert color="gray" icon={<IconAlertCircle size={16} />}>
              No limbo Policies available. A Policy enters limbo when it is
              detached from its Agreement. Use <em>Create new Policy</em>
              instead.
            </Alert>
          )}

          {(limboQ.data?.items.length ?? 0) > 0 && (
            <>
              <Text size="sm" c="dimmed">
                Limbo Policies available to attach to this Agreement:
              </Text>
              <Select
                placeholder="Select a Policy…"
                data={(limboQ.data?.items ?? []).map((p) => ({
                  value: p.id,
                  label: `${p.insurer} — ${p.policy_number}`,
                }))}
                value={selectedPolicyId}
                onChange={(v) => setSelectedPolicyId(v ?? '')}
                searchable
                allowDeselect={false}
              />
              {selectedPolicyId && (
                <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                  Attach moves the Policy back to <Badge color="green"
                  variant="light">Active</Badge> bound to this Agreement.
                  Indirect Accounts owned by the Policy stay <Badge
                  color="yellow" variant="light">Inactive</Badge>.
                </Alert>
              )}
            </>
          )}

          <Group justify="space-between" pt="sm">
            <Button variant="default" onClick={() => setPath('choose')}
              disabled={attachMut.isPending}>
              Back
            </Button>
            <Button leftSection={<IconLink size={14} />}
              onClick={handleAttach}
              disabled={!selectedPolicyId || attachMut.isPending}
              loading={attachMut.isPending}>
              {onStageAttach ? 'Stage attach' : 'Attach Policy'}
            </Button>
          </Group>
        </Stack>
      )}

      {path === 'move' && (
        <Stack>
          {activeQ.isLoading && <Text c="dimmed" size="sm">Loading Policies…</Text>}

          {!activeQ.isLoading && moveCandidates.length === 0 && (
            <Alert color="gray" icon={<IconAlertCircle size={16} />}>
              No Active Policies on other Agreements available to move. Use
              <em> Attach existing limbo Policy</em> or <em>Create new
              Policy</em> instead.
            </Alert>
          )}

          {moveCandidates.length > 0 && (
            <>
              <Text size="sm" c="dimmed">
                Active Policies on other Agreements — pick one to move here:
              </Text>
              <Select
                placeholder="Select a Policy…"
                data={moveCandidates.map((p) => ({
                  value: p.id,
                  label: `${p.insurer} — ${p.policy_number}`,
                }))}
                value={selectedPolicyId}
                onChange={(v) => setSelectedPolicyId(v ?? '')}
                searchable
                allowDeselect={false}
              />

              {selectedPolicyId && movePreviewQ.isLoading && (
                <Text size="sm" c="dimmed">Loading move preview…</Text>
              )}

              {selectedPolicyId && movePreviewQ.error && (
                <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                  {movePreviewQ.error instanceof ApiError
                    ? movePreviewQ.error.detail
                    : 'Failed to load move preview.'}
                </Alert>
              )}

              {selectedPolicyId && movePreviewQ.data && (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm" mb={6}>
                    <strong>{movePreviewQ.data.policy_label}</strong> will
                    move from{' '}
                    <strong>{movePreviewQ.data.source_agreement_label ?? '(limbo)'}</strong>
                    {' → '}
                    <strong>{movePreviewQ.data.target_agreement_label}</strong>.
                  </Text>
                  {movePreviewQ.data.indirect_accounts.length > 0 ? (
                    <Text size="sm">
                      <strong>{movePreviewQ.data.indirect_accounts.length}</strong>{' '}
                      Active Indirect Account
                      {movePreviewQ.data.indirect_accounts.length === 1 ? '' : 's'}{' '}
                      owned by this Policy will cascade to{' '}
                      <Badge color="yellow" variant="light">Inactive</Badge>.
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No Active Indirect Accounts to cascade.
                    </Text>
                  )}
                  <Text size="xs" c="dimmed" mt={6}>
                    RM filings queue a Change Request; Compliance/Admin
                    auto-apply. Re-attaching the Policy later does NOT
                    auto-reactivate the cascaded Accounts.
                  </Text>
                </Alert>
              )}
            </>
          )}

          <Group justify="space-between" pt="sm">
            <Button variant="default" onClick={() => setPath('choose')}
              disabled={moveMut.isPending}>
              Back
            </Button>
            <Button leftSection={<IconArrowsRightLeft size={14} />}
              onClick={handleMove}
              disabled={!selectedPolicyId || !movePreviewQ.data || moveMut.isPending}
              loading={moveMut.isPending}
              color="yellow">
              {onStageMove ? 'Stage move' : 'Move Policy'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
